import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MoveHorizontal } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const ANGLES = [45, 90, 135, 180, 225, 270, 315, 0];

const CARS = {
  aero: { label: "AERO D", frames: ANGLES.map((a) => `/images/spin-aero-${a}.jpg`) },
  suv: { label: "SUV", frames: ANGLES.map((a) => `/images/spin-suv-${a}.jpg`) },
  electric: { label: "ELECTRIC", frames: ANGLES.map((a) => `/images/spin-${a}.jpg`) },
};
const CAR_ORDER = ["aero", "suv", "electric"];

function angleName(a) {
  if (a === 0) return "Front";
  if (a === 90) return "Side";
  if (a === 180) return "Rear";
  if (a === 270) return "Side";
  return a < 180 ? "Front ¾" : "Rear ¾";
}

export default function SpinView({ heading }) {
  const [carId, setCarId] = useState(CAR_ORDER[0]);
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [spinning, setSpinning] = useState(true);
  const [grabbing, setGrabbing] = useState(false);
  const [speed, setSpeed] = useState("normal");
  const dragRef = useRef(null);
  const idleRef = useRef(null);
  const frames = CARS[carId].frames;

  const SPEED_MS = { slow: 3600, normal: 2200, fast: 1100 };

  useEffect(() => {
    setLoaded(false);
    let done = 0;
    frames.forEach((src) => {
      const im = new Image();
      im.onload = im.onerror = () => {
        done += 1;
        if (done === frames.length) setLoaded(true);
      };
      im.src = src;
    });
  }, [carId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loaded || !spinning) return undefined;
    const iv = setInterval(() => setIdx((i) => (i + 1) % frames.length), SPEED_MS[speed]);
    return () => clearInterval(iv);
  }, [loaded, spinning, carId, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  const wake = () => {
    setSpinning(false);
    clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => setSpinning(true), 4000);
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, startIdx: idx };
    setGrabbing(true);
    wake();
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const steps = Math.round(dx / 45);
    if (steps !== 0) {
      const next = (((dragRef.current.startIdx + steps) % frames.length) + frames.length) % frames.length;
      setIdx(next);
    }
  };

  const onPointerUp = () => {
    dragRef.current = null;
    setGrabbing(false);
    wake();
  };

  const step = (d) => {
    setIdx((i) => (i + d + frames.length) % frames.length);
    wake();
  };

  const switchCar = (id) => {
    if (id === carId) return;
    setCarId(id);
    setIdx(0);
    wake();
  };

  const h = heading || {
    eyebrow: "360° Spin View",
    title: "घुमाइए, अपनी नज़र से देखिए",
    sub: "Car पर drag कीजिए या arrows दबाइए — AERO D, SUV या Electric, हर IshuCar हर angle से अपना जादू दिखाती है।",
  };

  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="spin-section">
      <SectionHeading eyebrow={h.eyebrow} title={h.title} sub={h.sub} />
      <Reveal className="mt-8 flex justify-center">
        <div className="inline-flex flex-wrap justify-center rounded-full border border-white/10 bg-[#101014] p-1" data-testid="spin-car-tabs">
          {CAR_ORDER.map((id) => (
            <button
              key={id}
              onClick={() => switchCar(id)}
              className={`rounded-full px-5 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-200 sm:px-6 ${
                carId === id ? "bg-[#e6b800] font-bold text-black" : "text-neutral-400 hover:text-white"
              }`}
              data-testid={`spin-car-tab-${id}`}
            >
              {CARS[id].label}
            </button>
          ))}
        </div>
      </Reveal>
      <Reveal className="mt-10">
        <div
          className={`relative overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0e] ${grabbing ? "cursor-grabbing" : "cursor-grab"} touch-none select-none`}
          data-testid="spin-view"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <div className="relative h-[260px] sm:h-[480px]">
            {frames.map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`IshuCar ${CARS[carId].label} — ${ANGLES[i]}° view`}
                draggable="false"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${loaded && i === idx ? "opacity-100" : "opacity-0"}`}
                data-testid={i === idx ? "spin-image" : undefined}
              />
            ))}
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0e]" data-testid="spin-loading">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#e6b800]/30 border-t-[#e6b800]" />
              </div>
            )}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-[#070709] via-[#070709]/40 to-transparent p-4 sm:p-6">
              <p className="font-display text-lg font-bold text-white sm:text-xl" data-testid="spin-angle">
                {CARS[carId].label} · {ANGLES[idx]}° · {angleName(ANGLES[idx])}
              </p>
              {!grabbing && (
                <p className="flex items-center gap-2 rounded-full border border-white/20 bg-black/40 px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.2em] text-neutral-300 backdrop-blur-sm" data-testid="spin-hint">
                  <MoveHorizontal size={13} className="text-[#e6b800]" />
                  Drag करके घुमाओ
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
          <button
            onClick={() => step(-1)}
            data-testid="spin-prev"
            aria-label="Previous angle"
            className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-neutral-300 transition-colors duration-200 hover:border-[#e6b800] hover:text-[#e6b800]"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-1.5" data-testid="spin-dots">
            {frames.map((_, i) => (
              <button
                key={i}
                onClick={() => { setIdx(i); wake(); }}
                aria-label={`Go to ${ANGLES[i]}° view`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#e6b800]" : "w-1.5 bg-neutral-700 hover:bg-neutral-500"}`}
              />
            ))}
          </div>
          <button
            onClick={() => step(1)}
            data-testid="spin-next"
            aria-label="Next angle"
            className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-neutral-300 transition-colors duration-200 hover:border-[#e6b800] hover:text-[#e6b800]"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="mt-4 flex items-center justify-center gap-3" data-testid="spin-speed-control">
          <span className="font-mono2 text-[9px] uppercase tracking-[0.22em] text-neutral-500">Spin speed</span>
          <div className="inline-flex rounded-full border border-white/10 bg-[#101014] p-0.5">
            {[
              { id: "slow", label: "Slow" },
              { id: "normal", label: "Normal" },
              { id: "fast", label: "Fast" },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => { clearTimeout(idleRef.current); setSpeed(s.id); setSpinning(true); }}
                className={`rounded-full px-4 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] transition-colors duration-200 ${
                  speed === s.id ? "bg-[#e6b800] font-bold text-black" : "text-neutral-400 hover:text-white"
                }`}
                data-testid={`spin-speed-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
